import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Badge, Icon, Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import {
  BadgeSlot,
  CardPrice,
  PLAN_CARD_SHELL,
  PlanDetailsAccordion,
} from './PlanCardParts'
import {
  billingLineCopy,
  useBaseTierDisplayNames,
  discountExpiryCopy,
  formatPlanDate,
  isAnnualPaidMonthly,
  perMonthCents,
} from './lib/helpers'
import type { CurrentPlan } from './lib/planChange.types'

export interface CurrentPlanCardProps {
  currentPlan: CurrentPlan
  displayName?: string
  /** Who the plan is for, shown only when the view mixes customer types. */
  eyebrow?: string | null
  includedFeatureIds: string[] | null
  hasPendingChange: boolean
  reserveBadgeRow?: boolean
}

const nextDateCopy = (currentPlan: CurrentPlan): string => {
  if (currentPlan.cadence === 'annual') {
    return `Renews ${formatPlanDate(currentPlan.currentPeriodEnd)}`
  }
  const nextBill = `Next bill ${formatPlanDate(currentPlan.currentPeriodEnd)}`
  return isAnnualPaidMonthly(currentPlan)
    ? `${nextBill} · annual term ends ${formatPlanDate(currentPlan.termEndsAt)}`
    : nextBill
}

export const CurrentPlanCard = ({
  currentPlan,
  displayName,
  eyebrow = null,
  includedFeatureIds,
  hasPendingChange,
  reserveBadgeRow = false,
}: CurrentPlanCardProps) => {
  const names = useBaseTierDisplayNames()
  return (
    <div
      data-testid="plan-option-current"
      className={mtc(
        PLAN_CARD_SHELL,
        '@container border-transparent bg-neutral-25'
      )}
    >
      <BadgeSlot reserve={reserveBadgeRow} isRecommended={false} />
      <div className="flex min-h-8 items-center">
        <Text variant={TypographyStyles.DISPLAY_XS}>
          {displayName ?? names[currentPlan.tier]}
        </Text>
      </div>
      {eyebrow && (
        <Text
          testId="plan-audience"
          variant={TypographyStyles.EYEBROW}
          className="mt-2"
        >
          {eyebrow}
        </Text>
      )}

      <div className="mt-4 flex flex-col gap-1">
        <CardPrice
          effectiveAmountCents={perMonthCents(
            currentPlan.effectiveAmountCents,
            currentPlan.cadence
          )}
          listAmountCents={perMonthCents(
            currentPlan.listAmountCents,
            currentPlan.cadence
          )}
          cadence="monthly"
          strikeListPrice={
            currentPlan.effectiveAmountCents !== currentPlan.listAmountCents
          }
        />
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
          {billingLineCopy(
            currentPlan.cadence,
            currentPlan.effectiveAmountCents
          )}
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
          {nextDateCopy(currentPlan)}
        </Text>
        {hasPendingChange && (
          <div>
            <Badge size="sm" variant="secondary" color="yellow">
              Change scheduled
            </Badge>
          </div>
        )}
        {currentPlan.discountLabel && (
          <Text variant={TypographyStyles.BODY_SM} className="text-moss-200">
            {`Your ${currentPlan.discountLabel} is applied`}
          </Text>
        )}
        {currentPlan.discountLabel && currentPlan.discountEndsAt && (
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
            {discountExpiryCopy(currentPlan.discountEndsAt)}
          </Text>
        )}
      </div>

      <div className="my-4 h-px bg-neutral-100" />

      {includedFeatureIds !== null && (
        <PlanDetailsAccordion featureIds={includedFeatureIds} tone="keep" />
      )}

      {hasPendingChange && (
        <Text
          variant={TypographyStyles.BODY_SM}
          className="mt-auto pt-6 text-neutral-500"
        >
          Your scheduled change is shown above.
        </Text>
      )}

      <div className={hasPendingChange ? 'pt-3' : 'mt-auto pt-[22px]'}>
        <div className="flex h-10 items-center justify-center gap-2 rounded-lg border border-neutral-200">
          <Icon icon={faCheck} color="text-neutral-700" className="text-sm" />
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-700">
            Your current plan
          </Text>
        </div>
      </div>
    </div>
  )
}
